import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { useTranslation } from 'react-i18next';

import { Avatar, createLoadingShimmerStyles } from '@bloomays-lib/ui.shared';
import { TalentBoardEventType, type ApolloGqlAccountManager } from '@bloomays-lib/types.shared';
import { Logger } from '../../services/service-logger';

const logger = Logger('Contact');

type ContactProps = {
  contact: ApolloGqlAccountManager | null;
  loading: boolean;
};

const Contact = ({ contact, loading }: ContactProps) => {
  const { t } = useTranslation('talentBoard');

  if (loading)
    return (
      <ContactContainer>
        <ContactTitle>{t('sideBar.contactTitle')}</ContactTitle>
        <ContactInfo loading={loading}>
          <Avatar size="medium" alt="..." image={''} />
          <ContactDetails>
            <Typography variant="body2Medium">...</Typography>
            <Typography variant="mini">...</Typography>
            <Typography variant="mini">...</Typography>
          </ContactDetails>
        </ContactInfo>
      </ContactContainer>
    );

  if (contact === null) return null;

  const contactName = `${contact.firstname} ${contact.lastname}`;
  const contactEmail = contact.email;
  const contactPhone = contact.phoneNumber;
  const contactAvatar = contact.avatarUrl;

  return (
    <ContactContainer>
      <ContactTitle>{t('sideBar.contactTitle')}</ContactTitle>
      <ContactInfo loading={false}>
        <Avatar size="medium" alt={contactName} image={contactAvatar} />
        <ContactDetails>
          <ContactLine>
            <MailOutlineIcon fontSize="small" />
            <Typography
              variant="body2Medium"
              component="a"
              href={`mailto:${contactEmail}`}
              sx={{ color: 'inherit', textDecoration: 'none' }}
              onClick={() => {
                logger.info('Traffic', {
                  page: 'TalentBoard',
                  evt: { name: TalentBoardEventType.TalentboardOpenContactEmail },
                });
              }}
            >
              {contactName}
            </Typography>
          </ContactLine>
          {contactPhone && (
            <ContactLine>
              <LocalPhoneIcon fontSize="small" />
              <Typography
                variant="caption"
                component="a"
                href={`tel:${contactPhone}`}
                sx={{ color: 'inherit', textDecoration: 'none' }}
                onClick={() => {
                  logger.info('Traffic', {
                    page: 'TalentBoard',
                    evt: { name: TalentBoardEventType.TalentboardOpenContactPhone },
                  });
                }}
              >
                {contactPhone}
              </Typography>
            </ContactLine>
          )}
        </ContactDetails>
      </ContactInfo>
    </ContactContainer>
  );
};

const ContactContainer = styled('div')(({ theme }) => ({
  width: '100%',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const ContactTitle = styled('div')({
  width: '100%',
  fontFamily: '"Poppins SemiBold", sans-serif',
  fontSize: '14px',
  marginBottom: '12px',
});

const ContactInfo = styled('div', { shouldForwardProp: (prop) => prop !== 'loading' })<{ loading: boolean }>(
  ({ loading }) => ({
    display: 'flex',
    gap: '8px',
    width: '100%',
    alignItems: 'flex-start',
    minWidth: 0,
    ...createLoadingShimmerStyles({ active: loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
  }),
);

const ContactDetails = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  '& .MuiTypography-root': {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  },
});

const ContactLine = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',

  '&:hover': {
    cursor: 'pointer',
  },
});

export default Contact;
